/**
 * What Organisaties shows per column of the register (spec-63 E2, design §4.3). The register decides
 * which columns exist, in which order and which ones sort; this file only knows how to draw a cell of
 * an `OrganisationRow`. A column of the register that is not in the map is not shown.
 */
import { LabelChipList } from '../components/LabelChip'
import { CategoryCell, DayCell, Dash, OwnerCell, PersonName, PlainText, RelativeTime } from '../list/commonCells'
import { LastActivityCell } from '../list/LastActivityCell'
import { LastContactCell } from '../list/LastContactCell'
import { NextActionCell } from '../list/NextActionCell'
import { RhythmCell } from '../list/RhythmCell'
import { labelOf, SOURCE_LABELS, STATUS_LABELS } from '../list/optionLabels'
import type { ColumnRender } from '../list/RecordListPage'
import type { OrganisationRow } from '../query/types'
import { ContactsCountCell, OrganisationNameCell, WebsiteCell } from './OrganisationCells'

const COLUMNS: Record<string, ColumnRender<OrganisationRow>> = {
  name: { header: 'Organisatie', cell: (row) => <OrganisationNameCell row={row} /> },
  website: { header: 'Website', cell: (row) => <WebsiteCell website={row.website} /> },
  kvk: { header: 'KvK', cell: (row) => <PlainText value={row.kvk_number} /> },
  city: { header: 'Plaats', cell: (row) => <PlainText value={row.city} /> },
  phone: { header: 'Telefoon', cell: (row) => <PlainText value={row.phone} /> },
  email: { header: 'E-mail', cell: (row) => <PlainText value={row.email} /> },
  category: { header: 'Categorie', cell: (row) => <CategoryCell category={row.category} /> },
  labels: { header: 'Labels', cell: (row) => <LabelChipList labels={row.labels} /> },
  owner: { header: 'Eigenaar', cell: (row) => <OwnerCell owner={row.owner} /> },
  status: { header: 'Status', cell: (row) => <PlainText value={labelOf(STATUS_LABELS, row.status)} /> },
  relation_since: { header: 'Relatie sinds', cell: (row) => <DayCell day={row.relation_since} /> },
  source: { header: 'Bron', cell: (row) => <PlainText value={labelOf(SOURCE_LABELS, row.source)} /> },
  contacts_count: { header: 'Contactpersonen', cell: (row) => <ContactsCountCell row={row} /> },
  last_contact_at: { header: 'Laatste contact', cell: (row) => <LastContactCell lastContactAt={row.last_contact_at} /> },
  // The cell collapses into a narrow column and needs a width of its own to show who and via whom.
  last_activity_at: { header: 'Laatste activiteit', className: 'min-w-60', cell: (row) => <LastActivityCell activity={row.last_activity} /> },
  next_action_on: { header: 'Volgende actie', cell: (row) => <NextActionCell action={row.next_action} /> },
  open_tasks: { header: 'Open taken', cell: (row) => <PlainText value={row.open_tasks} /> },
  agreements_active: { header: 'Afspraken', cell: (row) => <PlainText value={row.agreements_active || null} /> },
  agreement_next_due: { header: 'Volgende afspraakdeadline', cell: (row) => <DayCell day={row.agreement_next_due} /> },
  rhythm_status: { header: 'Contactritme', cell: (row) => <RhythmCell status={row.rhythm_status} /> },
  created_at: { header: 'Aangemaakt', cell: (row) => <RelativeTime iso={row.created_at} /> },
  created_by: { header: 'Aangemaakt door', cell: (row) => (row.created_by ? <PersonName name={row.created_by.full_name} /> : <Dash />) },
  updated_at: { header: 'Rij gewijzigd', cell: (row) => <RelativeTime iso={row.updated_at} /> },
}

export function organisationColumns(): Record<string, ColumnRender<OrganisationRow>> {
  return COLUMNS
}
